import type { RecordActionMethod } from '@sentra/contracts';
import { Printer } from 'lucide-react';
import { type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiRoutes } from '@/api/routes';
import { Button } from '@/design/components/button';
import { useApiClient } from '@/features/auth/session';

/**
 * Printing the A4 pass sheet, which is never on the critical path.
 *
 * **The sheet is rendered by the SERVER and printed here.** It carries a QR symbol at a measured
 * geometry, an exact `@page size`, and exactly four values — the symbol, the grouped code, the
 * holder's first name and their record reference. A second implementation of that layout in the
 * browser would be a second answer to "what is on a pass", with the one nobody looks at being the
 * one that drifts; and the print is a separately AUDITED action from the reveal, because an
 * installation running pre-printed bands has to be able to show a count of zero prints.
 *
 * **A failure changes nothing.** The credential exists whether or not a sheet came out of a
 * printer, the code stays retrievable from the record at any time, and a jammed printer at a busy
 * desk must never be a reason a registration has to be done again. So this reports and stops; it
 * does not retry, and it does not touch the record.
 *
 * **The document is printed from an isolated frame, not from this page.** `srcdoc` on a
 * same-origin frame inherits this page's `Content-Security-Policy`, which the sheet is built to
 * satisfy: no script, no external stylesheet and no web font, so it renders offline at the exact
 * size it was measured at. Printing the application's own window instead would print the
 * application.
 */

/** Where a rendered document goes to be printed. Injected, because `jsdom` has no print. */
export interface PrintSurface {
  print: (html: string) => Promise<void>;
}

/** What the button is doing. A failure is a sentence, never a thrown error at a desk. */
type PrintState = 'idle' | 'working' | 'failed';

/**
 * The real surface: a hidden same-origin frame that prints itself and is then removed.
 *
 * The frame is created per print rather than kept: a document left in the tree is a live pass code
 * sitting in the DOM of a shared terminal for the rest of the shift.
 */
export function framePrintSurface(document: Document): PrintSurface {
  return {
    print: (html: string) =>
      new Promise<void>((resolve, reject) => {
        const frame = document.createElement('iframe');
        frame.setAttribute('aria-hidden', 'true');
        frame.style.position = 'fixed';
        frame.style.width = '0';
        frame.style.height = '0';
        frame.style.border = '0';
        // Both outcomes are wired: a frame whose document never loads would otherwise leave the
        // button saying it is working for the rest of the shift, with no way to try again.
        frame.addEventListener('error', () => {
          frame.remove();
          reject(new Error('the print frame could not load the sheet'));
        });
        frame.addEventListener('load', () => {
          try {
            const view = frame.contentWindow;
            if (view === null) {
              throw new Error('the print frame has no window');
            }
            view.focus();
            view.print();
            resolve();
          } catch (error) {
            reject(error instanceof Error ? error : new Error('the sheet could not be printed'));
          } finally {
            frame.remove();
          }
        });
        document.body.append(frame);
        frame.srcdoc = html;
      }),
  };
}

export function PrintPassButton({
  credentialId,
  firstName,
  method,
  surface,
}: Readonly<{
  credentialId: string;
  /** The holder's first name, which the sheet carries and the credential domain cannot read. */
  firstName: string;
  /** How this action was reached (AD-53), which is what the audit row records. */
  method: RecordActionMethod;
  surface: PrintSurface;
}>): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();
  const [state, setState] = useState<PrintState>('idle');

  const print = useCallback(() => {
    setState('working');
    void (async () => {
      try {
        const sheet = await client.request(apiRoutes.credentialPrint, {
          params: { id: credentialId },
          body: { firstName, method },
        });
        await surface.print(sheet.html);
        setState('idle');
      } catch {
        // Deliberately swallowed into a state rather than re-thrown: the pass is issued, the code
        // is on the record, and the only thing that failed is a sheet of paper.
        setState('failed');
      }
    })();
  }, [client, credentialId, firstName, method, surface]);

  return (
    <div className="grid gap-1">
      <Button
        type="button"
        variant="outline"
        data-testid="print-pass"
        disabled={state === 'working'}
        onClick={print}
      >
        <Printer aria-hidden />
        {t(state === 'working' ? 'scan.delivery.printing' : 'scan.delivery.print')}
      </Button>
      {state === 'failed' && (
        <p role="alert" className="text-sm text-danger">
          {t('scan.delivery.printFailed')}
        </p>
      )}
    </div>
  );
}
